import React, { useState } from 'react';
import { App, Button, Card, Input, Modal, Radio, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, ImportOutlined, PlusOutlined, StopOutlined, TagsOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import BulkActionsBar from '@/components/BulkActions/BulkActionsBar';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { useRecruitList } from './shared/useRecruitList';
import ReasonPrompt from './shared/ReasonPrompt';
import { bulkSummary, CANDIDATE_SOURCES, exportRows, parseJsonArray, recruitErrorMessage, stageLabel } from './shared/recruitUi';

const { Text } = Typography;
const FILTERS = ['source', 'tag', 'blacklisted', 'talent_pool'] as const;

const tagsOf = (v: unknown) => parseJsonArray<string>(v);

const CandidatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const list = useRecruitList(FILTERS);
  const [selected, setSelected] = useState<string[]>([]);
  const [tagModal, setTagModal] = useState(false);
  const [tagMode, setTagMode] = useState<'add' | 'remove'>('add');
  const [tagValues, setTagValues] = useState<string[]>([]);
  const [blacklistOpen, setBlacklistOpen] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.candidates(list.apiParams),
    queryFn: () => hrmsService.pageCandidates(list.apiParams),
    placeholderData: keepPreviousData,
  });
  const { data: tagOptions = [] } = useQuery({ queryKey: hrmsKeys.candidateTags(), queryFn: () => hrmsService.listCandidateTags() });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidates() });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidateTags() });
  };

  const report = (label: string, r: any) => {
    const s = bulkSummary(label, r);
    if (s.type === 'success') message.success(s.text);
    else message.warning(s.text);
  };

  const exportAll = async () => {
    const all = await hrmsService.pageCandidates({ ...list.apiParams, page: 1, limit: 5000 });
    await exportRows(
      'candidates',
      [
        ['candidate_number', 'Candidate #'],
        ['full_name', 'Name'],
        ['email', 'Email'],
        ['phone', 'Phone'],
        ['nationality', 'Nationality'],
        ['current_location', 'Location'],
        ['current_title', 'Current title'],
        ['current_employer', 'Current employer'],
        ['experience_years', 'Experience (yrs)'],
        ['skills', 'Skills'],
        ['source', 'Source'],
        ['agency_name', 'Agency'],
        ['tags', 'Tags'],
        ['applications', 'Applications'],
        ['latest_stage', 'Latest stage'],
        ['created_at', 'Added'],
      ],
      all.rows.map((r: any) => ({ ...r, tags: tagsOf(r.tags_json).join(', ') }))
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Candidate #',
      dataIndex: 'candidate_number',
      key: 'candidate_number',
      width: 130,
      fixed: 'left',
      sorter: true,
      sortOrder: list.sortOrderFor('candidate_number'),
      render: (v: string, r) => <a onClick={() => navigate(`/hr/candidates/${r.id}`)}>{v}</a>,
    },
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      ellipsis: true,
      sorter: true,
      sortOrder: list.sortOrderFor('full_name'),
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <span>
            {v} {r.is_blacklisted ? <Tag color="red">Blacklisted</Tag> : null}
            {r.is_internal ? <Tag color="blue">Internal</Tag> : null}
          </span>
          {r.current_title && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.current_title}
              {r.current_employer ? ` · ${r.current_employer}` : ''}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 220,
      ellipsis: true,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span>{r.email || '—'}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.phone || ''}
          </Text>
        </Space>
      ),
    },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 100, sorter: true, sortOrder: list.sortOrderFor('source') },
    {
      title: 'Tags',
      dataIndex: 'tags_json',
      width: 200,
      render: (v) => (
        <Space size={[4, 4]} wrap>
          {tagsOf(v).map((t) => (
            <Tag key={t} style={{ cursor: 'pointer' }} onClick={() => list.setFilter('tag', t)}>
              {t}
            </Tag>
          ))}
        </Space>
      ),
    },
    { title: 'Apps', dataIndex: 'applications', width: 70 },
    {
      title: 'Latest stage',
      dataIndex: 'latest_stage',
      width: 120,
      render: (s) => (s ? <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> : '—'),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Candidates"
          subtitle="Talent pool — one record per person, de-duplicated by email and phone"
          primaryActions={
            <Space>
              <Button icon={<DownloadOutlined />} onClick={exportAll}>
                Export
              </Button>
              <Button icon={<ImportOutlined />} onClick={() => navigate('/hr/candidates/import')}>
                Import
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/candidates/new')}>
                New candidate
              </Button>
            </Space>
          }
        />
      }
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Name, email, phone, skills"
            defaultValue={list.q}
            onSearch={list.setQ}
            style={{ width: 260 }}
          />
          <Select
            allowClear
            placeholder="Source"
            style={{ width: 140 }}
            value={list.filters.source}
            onChange={(v) => list.setFilter('source', v)}
            options={CANDIDATE_SOURCES.map((v) => ({ value: v, label: v }))}
          />
          <Select
            allowClear
            showSearch
            placeholder="Tag"
            style={{ width: 170 }}
            value={list.filters.tag}
            onChange={(v) => list.setFilter('tag', v)}
            options={tagOptions.map((t) => ({ value: t, label: t }))}
          />
          <Select
            allowClear
            placeholder="Talent pool"
            style={{ width: 150 }}
            value={list.filters.talent_pool}
            onChange={(v) => list.setFilter('talent_pool', v)}
            options={[{ value: 'true', label: 'In talent pool' }]}
          />
          <Select
            allowClear
            placeholder="Blacklist"
            style={{ width: 150 }}
            value={list.filters.blacklisted}
            onChange={(v) => list.setFilter('blacklisted', v)}
            options={[
              { value: 'false', label: 'Not blacklisted' },
              { value: 'true', label: 'Blacklisted' },
            ]}
          />
          {list.activeFilterCount > 0 && (
            <Button type="link" onClick={list.reset}>
              Clear filters
            </Button>
          )}
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.rows || []}
        scroll={{ x: 1200 }}
        pagination={list.pagination(data?.total || 0)}
        onChange={list.onTableChange}
        rowSelection={{ selectedRowKeys: selected, onChange: (k) => setSelected(k as string[]), preserveSelectedRowKeys: true }}
      />

      <BulkActionsBar
        selectedCount={selected.length}
        totalCount={data?.total || 0}
        selectedIds={selected}
        onClearSelection={() => setSelected([])}
        includeDefaultActions={false}
        actions={[
          {
            key: 'tags',
            label: 'Tag / talent pool',
            icon: <TagsOutlined />,
            selfReporting: true,
            onClick: () => {
              setTagValues([]);
              setTagMode('add');
              setTagModal(true);
            },
          },
          {
            key: 'blacklist',
            label: 'Blacklist',
            icon: <StopOutlined />,
            danger: true,
            selfReporting: true,
            onClick: () => setBlacklistOpen(true),
          },
          {
            key: 'export',
            label: 'Export selected',
            icon: <DownloadOutlined />,
            selfReporting: true,
            onClick: async (ids) => {
              const rows = (data?.rows || []).filter((r: any) => ids.includes(r.id));
              await exportRows(
                'candidates-selected',
                [
                  ['candidate_number', 'Candidate #'],
                  ['full_name', 'Name'],
                  ['email', 'Email'],
                  ['phone', 'Phone'],
                  ['source', 'Source'],
                  ['latest_stage', 'Latest stage'],
                ],
                rows
              );
            },
          },
        ]}
      />

      <Modal
        open={tagModal}
        title={`Tags for ${selected.length} candidate(s)`}
        okText={tagMode === 'add' ? 'Add tags' : 'Remove tags'}
        onCancel={() => setTagModal(false)}
        onOk={async () => {
          try {
            const r = await hrmsService.bulkCandidateTags({ ids: selected, tags: tagValues, mode: tagMode });
            report(tagMode === 'add' ? 'Tags added' : 'Tags removed', r);
            setTagModal(false);
            setSelected([]);
            await refresh();
          } catch (e: any) {
            message.error(recruitErrorMessage(e));
          }
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Radio.Group value={tagMode} onChange={(e) => setTagMode(e.target.value)}>
            <Radio.Button value="add">Add</Radio.Button>
            <Radio.Button value="remove">Remove</Radio.Button>
          </Radio.Group>
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="e.g. Electrical pool, Arabic speaker"
            value={tagValues}
            onChange={setTagValues}
            options={tagOptions.map((t) => ({ value: t, label: t }))}
          />
        </Space>
      </Modal>

      <ReasonPrompt
        open={blacklistOpen}
        title={`Blacklist ${selected.length} candidate(s)`}
        okText="Blacklist"
        notesRequired
        notesLabel="Reason"
        onCancel={() => setBlacklistOpen(false)}
        onSubmit={async (v) => {
          try {
            const r = await hrmsService.bulkCandidateBlacklist({ ids: selected, blacklisted: true, reason: v.notes });
            report('Blacklisted', r);
            setBlacklistOpen(false);
            setSelected([]);
            await refresh();
          } catch (e: any) {
            message.error(recruitErrorMessage(e));
          }
        }}
      />
    </WorkspaceLayout>
  );
};

export default CandidatesPage;
